import clsx from "clsx";
import type { FC } from "react";
import type { Doc } from "sharedb/lib/client";

import type {
  AssetFieldType,
  PdfState,
  WorksheetType,
} from "../../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../../shared/worksheets/constants";
import { AssetTitle } from "../../../../../worksheets/components/Field/AssetField";
import { PdfPageToolbar } from "../../../../../worksheets/components/Field/PdfPageToolbar";
import { ZoomToolbar } from "../../../../../worksheets/components/Field/ZoomToolbar";
import { isPDFFile } from "../../../../../worksheets/helpers/files";

import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ArrowDownToLineIcon, PresentPlayIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectFeaturingPdfState } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { getDefaultPdfState, setPdfStateOnDoc } from "utils/asset";
import { usePresentation } from "utils/presentation";
import { Link } from "utils/routing";

type FeatureHeaderProps = {
  doc: Doc<WorksheetType>;
  blockIndex: number;
};

export const FeatureAssetHeader: FC<FeatureHeaderProps> = ({ doc, blockIndex }) => {
  const assetField = doc.data?.b?.[blockIndex]?.f[0] as AssetFieldType;
  const { name, size, fileType, url, pdfState } = assetField;
  const { isPresenting, isFollowing } = usePresentation();
  const featuringPdfState = useAppSelector(selectFeaturingPdfState);

  const currentPdfState = isPresenting ? pdfState || getDefaultPdfState() : featuringPdfState;
  const isPdf = isPDFFile(fileType);
  const dispatch = useAppDispatch();

  const onPdfStateChange = (newState: Partial<PdfState>) => {
    if (isPresenting) {
      setPdfStateOnDoc(doc, [BLOCKS_PATH, blockIndex, "f", 0], pdfState, newState);
    } else {
      dispatch(viewStateActions.setFeaturingPdfState(newState));
    }
  };

  return (
    <div
      className={clsx(
        "flex w-full min-w-0 items-center justify-between gap-2",
        isPdf && "max-sm:flex-col max-sm:items-stretch",
      )}
    >
      <AssetTitle icon={<PresentPlayIcon className="text-green" />} name={name} size={size} />

      <div className="flex shrink-0 items-center gap-2">
        {isPdf && (
          <>
            <ZoomToolbar
              setZoom={(zoom) => onPdfStateChange({ zoom, userUpdated: isFollowing })}
              zoom={currentPdfState.zoom}
            />

            <PdfPageToolbar
              pdfState={currentPdfState}
              setPdfState={(state) => onPdfStateChange({ ...state, userUpdated: isFollowing })}
            />
          </>
        )}

        <SquareIconButton as={Link} download={name} kind="tertiary" size="s" to={url || ""}>
          <ArrowDownToLineIcon />
        </SquareIconButton>
      </div>
    </div>
  );
};
